import GuideLayout from "./GuideLayout";

export default function HowToCreateEpsFile() {
  return (
    <GuideLayout
      title="How to Create an EPS File"
      description="Learn how to create an EPS file from scratch or export one from Illustrator, Inkscape, CorelDRAW and other tools. Covers settings, font handling, and common export mistakes."
      path="/guides/how-to-create-an-eps-file"
      updated="June 30, 2026"
      related={["/guides/what-is-an-eps-file", "/guides/eps-vs-svg-vs-ai-vs-pdf", "/guides/fix-eps-file-problems"]}
    >
      <p className="lead">
        EPS files are almost always created by exporting from a vector graphics editor.
        Here&apos;s how to create an EPS file from the most common tools — and what
        settings to choose to avoid common problems like missing fonts or broken
        transparency.
      </p>

      <h2>Before you start: do you actually need EPS?</h2>
      <p>
        EPS is the right choice when a printer, sign-maker, or embroidery machine
        specifically requests it, or when you are delivering a logo in a legacy format.
        For most other purposes, consider:
      </p>
      <ul>
        <li><strong>PDF</strong> for print delivery to modern printers — better transparency support.</li>
        <li><strong>SVG</strong> for web use — browsers can display SVG natively.</li>
        <li><strong>PNG</strong> for sharing artwork in documents, emails and presentations.</li>
      </ul>
      <p>If you&apos;ve confirmed EPS is what you need, read on.</p>

      <h2>Create an EPS file in Adobe Illustrator</h2>
      <p>Illustrator is the most common tool for creating EPS files and has the most reliable EPS export.</p>
      <ol>
        <li>Create your artwork in Illustrator as you normally would.</li>
        <li>Before exporting, <strong>outline all text</strong>: select all text → Type → Create Outlines. This converts text to vector shapes so the recipient doesn&apos;t need the same fonts installed.</li>
        <li>Go to <strong>File → Save As</strong> (not Export).</li>
        <li>Choose <strong>Illustrator EPS (.eps)</strong> from the format dropdown.</li>
        <li>Click Save. The EPS Options dialog appears.</li>
        <li>Key settings to choose:
          <ul>
            <li><strong>Version:</strong> choose EPS 8 or EPS 10 for broadest compatibility. Older versions (EPS 3) may be needed for very old equipment.</li>
            <li><strong>Preview:</strong> choose TIFF (8-bit Color) — this embeds a preview thumbnail so the file can be displayed in layout applications like InDesign.</li>
            <li><strong>Fonts:</strong> select &quot;Embed Fonts&quot; if you didn&apos;t outline the text. Outlining is more reliable.</li>
            <li><strong>Transparency:</strong> if your artwork has transparency, you must choose how to flatten it. Select &quot;Preserve Appearance&quot; for the best visual result.</li>
          </ul>
        </li>
        <li>Click OK. Your EPS file is saved.</li>
      </ol>

      <h2>Create an EPS file in Inkscape (free)</h2>
      <p>Inkscape can export EPS files via the built-in save function.</p>
      <ol>
        <li>Create or open your artwork in Inkscape.</li>
        <li>Outline any text: select the text → Path → Object to Path.</li>
        <li>Go to <strong>File → Save As</strong>.</li>
        <li>In the file type dropdown, choose <strong>Encapsulated PostScript (*.eps)</strong>.</li>
        <li>Click Save. An options dialog may appear — the defaults are usually fine for most uses.</li>
      </ol>
      <p>
        <strong>Note:</strong> Inkscape&apos;s EPS export is good but not perfect. Very complex artwork
        with many gradients may not export identically to Illustrator. For critical print work,
        Illustrator remains the most reliable tool.
      </p>

      <h2>Create an EPS file in CorelDRAW</h2>
      <ol>
        <li>Create your artwork in CorelDRAW.</li>
        <li>Convert text to curves: select text → Arrange → Convert to Curves (or press Ctrl+Q).</li>
        <li>Go to <strong>File → Export</strong>.</li>
        <li>Choose <strong>EPS - Encapsulated PostScript</strong> from the file type list.</li>
        <li>Click Export and configure the EPS settings in the dialog that appears.</li>
      </ol>

      <h2>Create an EPS file in Affinity Designer</h2>
      <ol>
        <li>Create your artwork in Affinity Designer.</li>
        <li>Convert any text to curves: select text → Layer → Convert to Curves.</li>
        <li>Go to <strong>File → Export</strong>.</li>
        <li>Select the <strong>EPS</strong> tab.</li>
        <li>Choose your settings and click Export.</li>
      </ol>

      <h2>Critical settings to get right</h2>
      <div className="table-wrap">
        <table className="tbl">
          <thead>
            <tr><th>Setting</th><th>Recommended choice</th><th>Why</th></tr>
          </thead>
          <tbody>
            <tr><td>Text handling</td><td>Outline/convert to curves before export</td><td>Prevents font substitution on other machines</td></tr>
            <tr><td>Preview thumbnail</td><td>TIFF 8-bit</td><td>Lets InDesign, Word etc. show a visual preview</td></tr>
            <tr><td>Colour mode</td><td>CMYK for print, RGB for screen</td><td>Printers use CMYK; screens use RGB</td></tr>
            <tr><td>Version</td><td>EPS 8 or EPS 10</td><td>Broadest compatibility with print equipment</td></tr>
            <tr><td>Transparency</td><td>Flatten/Preserve Appearance</td><td>EPS has no native transparency — must be flattened</td></tr>
          </tbody>
        </table>
      </div>

      <h2>Common mistakes when creating EPS files</h2>
      <ul>
        <li>
          <strong>Not outlining fonts:</strong> this is the most common cause of EPS files
          looking wrong on another machine. Always outline or embed your fonts before saving.
        </li>
        <li>
          <strong>Using RGB colour for a print file:</strong> if the EPS is going to a printer,
          make sure your colour mode is CMYK. RGB colours look bright on screen but can shift
          unpredictably when converted to print inks.
        </li>
        <li>
          <strong>Saving with &quot;Use Artboards&quot; unchecked in Illustrator:</strong> if your
          artwork is positioned correctly on an artboard, make sure the EPS is saved to match
          those bounds, otherwise it may export with unexpected white space.
        </li>
        <li>
          <strong>Assuming transparency will carry over:</strong> EPS does not support transparency.
          Shadows and semi-transparent overlays will be flattened — test how the file looks after
          export before sending it to a printer.
        </li>
        <li>
          <strong>Embedding high-resolution photos:</strong> if your EPS has embedded photos, the
          file size can become very large. For print work, consider placing photos as linked files
          instead of embedding them.
        </li>
      </ul>

      <h2>How to check your EPS file after creating it</h2>
      <ul>
        <li>Open the saved EPS in the <a href="/">online EPS Viewer</a> to confirm it renders correctly without your design software.</li>
        <li>Check that all text has been converted to outlines (if you intended to outline it).</li>
        <li>Zoom in to check that edges are sharp and crisp — blurry edges suggest raster content.</li>
        <li>Send the file to a colleague or client and ask them to open it — this confirms it opens correctly without your fonts or software installed.</li>
      </ul>

      <h2>Frequently asked questions</h2>
      <dl className="guide-faq">
        <dt>Can I create an EPS file in Microsoft Word or PowerPoint?</dt>
        <dd>No. Word and PowerPoint are not vector graphics editors and do not export EPS. You need a dedicated vector tool like Inkscape (free) or Illustrator.</dd>
        <dt>Should I use &quot;Save As&quot; or &quot;Export&quot; in Illustrator?</dt>
        <dd>Use <strong>File → Save As → EPS</strong> for the best results. The Export function in Illustrator is intended for raster formats like PNG and JPG.</dd>
        <dt>What EPS version should I use?</dt>
        <dd>EPS version 8 or 10 is suitable for almost all modern uses. If a print shop or machine requires a specific version, they will tell you — otherwise, EPS 8 is a safe default.</dd>
        <dt>How do I know if my EPS file will print correctly?</dt>
        <dd>Open it in Inkscape or the online EPS Viewer to verify the rendering. Ask your printer to send a soft proof (PDF preview) before final print run.</dd>
      </dl>
    </GuideLayout>
  );
}
